import baseApi from 'shared/api/baseApi';

import type { PromptSectionCategory } from '../lib/promptSectionCategories';

export type PromptSectionPermission = 'hidden' | 'read-only' | 'suggest-edit' | 'editable';

export interface GlobalPromptSection {
  id: number;
  name: string;
  displayName: string;
  value: string;
  permission: PromptSectionPermission;
  isLastVersion: boolean;
  isSuggestion: boolean;
  userId: number;
  createdAt: string;
  updatedAt: string;
  promptTag?: string;
  categories: PromptSectionCategory[];
}

export interface ProjectPromptSection {
  id: number;
  name: string;
  displayName: string;
  value: string;
  permission: PromptSectionPermission;
  promptTag?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GetGlobalPromptSectionsQuery {
  offset: number;
  limit: number;
  searchTerm?: string;
  categories?: PromptSectionCategory[];
}

export interface GetGlobalPromptSectionsResponse {
  globalPromptSections: GlobalPromptSection[];
  total: number;
  totalPages: number;
}

export interface GetProjectPromptSectionsQuery {
  projectId: number;
  offset: number;
  limit: number;
  searchTerm?: string;
}

export interface GetProjectPromptSectionsResponse {
  promptSections: ProjectPromptSection[];
  total: number;
  totalPages: number;
}

export interface CreateProjectPromptSectionBody {
  projectId: number;
  name: string;
  displayName: string;
  value: string;
  permission: PromptSectionPermission;
}

export interface UpdateProjectPromptSectionBody {
  id: number;
  projectId: number;
  name: string;
  displayName: string;
  value: string;
  permission: PromptSectionPermission;
}

export interface DeleteProjectPromptSectionBody {
  projectId: number;
  name: string;
}

export interface UpdateGlobalPromptSectionBody {
  id: number;
  displayName: string;
  value: string;
  permission: PromptSectionPermission;
}

const promptSectionsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getGlobalPromptSections: build.query<GetGlobalPromptSectionsResponse, GetGlobalPromptSectionsQuery>({
      query: ({ offset, limit, searchTerm, categories }) => {
        const params = new URLSearchParams();
        params.set('offset', offset.toString());
        params.set('limit', limit.toString());
        if (searchTerm && searchTerm.trim()) {
          params.set('searchTerm', searchTerm.trim());
        }
        if (categories && categories.length > 0) {
          params.set('categories', categories.join(','));
        }
        return { url: `/admin/globalPromptSections?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['GlobalPromptSections'],
    }),
    getProjectPromptSections: build.query<GetProjectPromptSectionsResponse, GetProjectPromptSectionsQuery>({
      query: ({ projectId, offset, limit, searchTerm }) => {
        const params = new URLSearchParams();
        params.set('projectId', projectId.toString());
        params.set('offset', offset.toString());
        params.set('limit', limit.toString());
        if (searchTerm && searchTerm.trim()) {
          params.set('searchTerm', searchTerm.trim());
        }
        return { url: `/admin/promptSections?${params.toString()}`, method: 'GET' };
      },
      providesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectPromptSections', id: projectId },
        'ProjectPromptSections',
      ],
    }),
    createProjectPromptSection: build.mutation<ProjectPromptSection, CreateProjectPromptSectionBody>({
      query: ({ projectId, name, displayName, value, permission }) => ({
        url: '/partner/prompt-sections',
        method: 'POST',
        body: {
          projectId,
          name,
          displayName,
          value,
          permission,
        },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectPromptSections', id: projectId },
        'ProjectPromptSections',
      ],
    }),
    updateProjectPromptSection: build.mutation<ProjectPromptSection, UpdateProjectPromptSectionBody>({
      query: ({ id, projectId, name, displayName, value, permission }) => ({
        url: `/partner/prompt-sections/${id}?projectId=${projectId}`,
        method: 'PATCH',
        body: {
          name,
          displayName,
          value,
          permission,
        },
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectPromptSections', id: projectId },
        'ProjectPromptSections',
      ],
    }),
    deleteProjectPromptSection: build.mutation<void, DeleteProjectPromptSectionBody>({
      query: ({ projectId, name }) => ({
        url: `/promptSections/deleteAll/${encodeURIComponent(name)}?projectId=${projectId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId }) => [
        { type: 'ProjectPromptSections', id: projectId },
        'ProjectPromptSections',
      ],
    }),
    updateGlobalPromptSection: build.mutation<GlobalPromptSection, UpdateGlobalPromptSectionBody>({
      query: ({ id, displayName, value, permission }) => ({
        url: `/admin/globalPromptSections/${id}`,
        method: 'PATCH',
        body: { displayName, value, permission },
      }),
      invalidatesTags: ['GlobalPromptSections'],
    }),
    setGlobalPromptSectionCategories: build.mutation<void, { name: string; categories: PromptSectionCategory[] }>({
      query: ({ name, categories }) => ({
        url: `/admin/globalPromptSections/categories/${encodeURIComponent(name)}`,
        method: 'PUT',
        body: { categories },
      }),
      invalidatesTags: ['GlobalPromptSections'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetGlobalPromptSectionsQuery,
  useGetProjectPromptSectionsQuery,
  useCreateProjectPromptSectionMutation,
  useUpdateProjectPromptSectionMutation,
  useDeleteProjectPromptSectionMutation,
  useUpdateGlobalPromptSectionMutation,
  useSetGlobalPromptSectionCategoriesMutation,
} = promptSectionsApi;

export default promptSectionsApi;
